import { useEffect, useState } from "react";
import api, { formatApiError, formatMoney, todayLocalIso } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Receipt, FileSpreadsheet, FileText, AlertTriangle } from "lucide-react";
import { exportToExcel, exportToPdf } from "@/lib/exportUtils";

export default function FacturasGastos() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const today = todayLocalIso();
  const firstDay = today.slice(0, 8) + "01";
  const [f, setF] = useState({ desde: firstDay, hasta: today, proveedor: "", categoria: "", numero_factura: "" });

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ desde: f.desde, hasta: f.hasta });
      const { data } = await api.get(`/gastos?${params}`);
      let rows = (data.items || []).filter(g => (g.numero_factura || g.imagen_base64 || g.total));
      if (f.proveedor) rows = rows.filter(g => (g.tienda || g.proveedor || "").toLowerCase().includes(f.proveedor.toLowerCase()));
      if (f.categoria) rows = rows.filter(g => (g.categoria || "").toLowerCase().includes(f.categoria.toLowerCase()));
      if (f.numero_factura) rows = rows.filter(g => (g.numero_factura || "").toLowerCase().includes(f.numero_factura.toLowerCase()));
      // Detectar duplicados (mismo proveedor + factura + monto)
      const map = {};
      rows.forEach(g => {
        const key = `${g.tienda || ""}|${g.numero_factura || ""}|${g.total}`;
        map[key] = (map[key] || 0) + 1;
      });
      rows = rows.map(g => ({ ...g, _dup: map[`${g.tienda || ""}|${g.numero_factura || ""}|${g.total}`] > 1 }));
      setItems(rows);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const columns = [
    { key: "fecha", label: "Fecha", type: "date" },
    { key: "numero_factura", label: "N° Factura" },
    { key: "tienda", label: "Proveedor" },
    { key: "categoria", label: "Categoría" },
    { key: "descripcion", label: "Detalle" },
    { key: "total", label: "Total", type: "money" },
    { key: "banco", label: "Banco" },
    { key: "caja_id", label: "Caja" },
    { key: "metodo_pago", label: "Método" },
    { key: "referencia", label: "Referencia" },
    { key: "created_by", label: "Usuario" },
  ];

  const sinFactura = items.filter(g => !g.numero_factura).length;
  const duplicados = items.filter(g => g._dup).length;

  return (
    <div className="space-y-6" data-testid="facturas-gastos-page">
      <header>
        <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500 font-semibold">Catálogo</div>
        <h1 className="font-manrope font-black text-4xl mt-1 flex items-center gap-3"><Receipt className="w-8 h-8" /> Facturas de Gastos</h1>
        <p className="text-zinc-500 text-sm mt-2">Todas las facturas de proveedores con estado, foto, banco/caja y detección de duplicados.</p>
      </header>

      <Card className="p-4 border-zinc-200">
        <div className="flex flex-wrap items-end gap-3">
          <div><Label>Desde</Label><Input type="date" value={f.desde} onChange={(e) => setF({ ...f, desde: e.target.value })} className="w-40" /></div>
          <div><Label>Hasta</Label><Input type="date" value={f.hasta} onChange={(e) => setF({ ...f, hasta: e.target.value })} className="w-40" /></div>
          <div><Label>Proveedor</Label><Input value={f.proveedor} onChange={(e) => setF({ ...f, proveedor: e.target.value })} className="w-48" /></div>
          <div><Label>N° Factura</Label><Input value={f.numero_factura} onChange={(e) => setF({ ...f, numero_factura: e.target.value })} className="w-40" /></div>
          <div><Label>Categoría</Label><Input value={f.categoria} onChange={(e) => setF({ ...f, categoria: e.target.value })} className="w-40" /></div>
          <Button onClick={load} className="bg-[#002FA7] hover:bg-[#00227A]">Filtrar</Button>
          <div className="flex-1"></div>
          <Button variant="outline" onClick={() => exportToExcel({ title: "Facturas de gastos", filters: f, columns, rows: items, filename: `facturas_gastos_${f.desde}_${f.hasta}` })} className="gap-2"><FileSpreadsheet className="w-4 h-4" />Excel</Button>
          <Button variant="outline" onClick={() => exportToPdf({ title: "Facturas de gastos", filters: f, columns, rows: items, filename: `facturas_gastos_${f.desde}_${f.hasta}` })} className="gap-2"><FileText className="w-4 h-4" />PDF</Button>
        </div>
        {(sinFactura > 0 || duplicados > 0) && (
          <div className="mt-3 flex gap-4 text-sm">
            {sinFactura > 0 && <div className="text-orange-700 flex items-center gap-1"><AlertTriangle className="w-4 h-4" /> {sinFactura} sin N° factura</div>}
            {duplicados > 0 && <div className="text-red-700 flex items-center gap-1"><AlertTriangle className="w-4 h-4" /> {duplicados} posibles duplicados</div>}
          </div>
        )}
      </Card>

      <Card className="border-zinc-200 overflow-hidden">
        <div className="p-4 border-b border-zinc-100 flex items-center justify-between">
          <div className="font-semibold">{items.length} facturas</div>
          <div className="text-right">
            <div className="text-[10px] uppercase text-zinc-500 font-semibold">Total</div>
            <div className="font-black text-2xl text-blue-700 tabular-nums">{formatMoney(items.reduce((s, x) => s + Number(x.total || 0), 0))}</div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-zinc-600">
              <tr className="text-left">
                <th className="px-3 py-2 text-[10px] uppercase font-semibold">Fecha</th>
                <th className="px-3 py-2 text-[10px] uppercase font-semibold">N° Factura</th>
                <th className="px-3 py-2 text-[10px] uppercase font-semibold">Proveedor</th>
                <th className="px-3 py-2 text-[10px] uppercase font-semibold">Categoría</th>
                <th className="px-3 py-2 text-[10px] uppercase font-semibold">Detalle</th>
                <th className="px-3 py-2 text-[10px] uppercase font-semibold text-right">Total</th>
                <th className="px-3 py-2 text-[10px] uppercase font-semibold">Banco/Caja</th>
                <th className="px-3 py-2 text-[10px] uppercase font-semibold">Alertas</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {loading && <tr><td colSpan={8} className="text-center text-zinc-400 py-8">Cargando…</td></tr>}
              {!loading && items.length === 0 && <tr><td colSpan={8} className="text-center text-zinc-400 py-10">Sin facturas.</td></tr>}
              {items.map((g) => (
                <tr key={g.id} className={`hover:bg-zinc-50 ${g._dup ? "bg-red-50/40" : ""}`}>
                  <td className="px-3 py-2 text-xs">{g.fecha}</td>
                  <td className="px-3 py-2 text-xs font-mono">{g.numero_factura || <span className="text-orange-600">Sin factura</span>}</td>
                  <td className="px-3 py-2 text-xs">{g.tienda || g.proveedor}</td>
                  <td className="px-3 py-2 text-xs">{g.categoria || "—"}</td>
                  <td className="px-3 py-2 text-xs">{g.descripcion || "—"}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold">{formatMoney(g.total)}</td>
                  <td className="px-3 py-2 text-xs">{g.caja_id ? "Caja" : (g.banco || "—")}</td>
                  <td className="px-3 py-2 text-xs">{g._dup && <span className="text-red-700 font-bold">DUPLICADO</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
